import * as Immutable from 'immutable';
import moment from 'moment';
import { handleActions } from 'redux-actions';

import { setUserData } from '../../shared/helpers/user';

import { AuthenticationActionType, AuthenticationStoreElement } from './AuthenticationReducer.types';

export const initialState = Immutable.fromJS({
  userId: null,
  inquiryId: null,
  isFetchingLogout: false,
  isFetching: false,
  isLoggedIn: false,
  permissions: Immutable.List(),
  data: null,
  settings: null,
  isFetchingSettings: false,
  isFetchingReverify: false,
  reverifySent: false,
  forgotPasswordEmailSent: false,
  isPasswordReset: false,
  timeToLogout: null,
  hasLoggedInBefore: false,
  fetchingAuth: null,
  fetchingChangePassword: false,
  fetchingUserAgreements: false,
  userAgreements: null,
  needsRecertification: false,
  showMessage: true,
  configuration: {
    analytics: { trackingId: null },
  },
  challenge: null,
  challengeUser: null,
  isSelfieError: false,
});

const checkifNeedsRecertification = (agreements: any) => {
  let needsRecertification = false;

  for (const elem of agreements) {
    if (
      elem.hasOwnProperty('agreement') &&
      elem.agreement.hasOwnProperty('id') &&
      elem.agreement.id === 'account-details' &&
      elem.hasOwnProperty('status') &&
      elem.status === 'REQUIRED'
    ) {
      needsRecertification = true;
    }
    if (needsRecertification === true) {
      break;
    }
  }

  return needsRecertification;
};

const AuthenticationReducer = handleActions(
  {
    AUTHENTICATION_REQUEST: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: true,
      }),
    AUTHENTICATION_REQUEST_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: false,
        fetchingAuth: false,
      }),
    REQUEST_RECERTIFICATION: (state: AuthenticationStoreElement) =>
      state.merge({
        fetchingAuth: null,
      }),
    AUTHENTICATION_LOGIN: (state: AuthenticationStoreElement, action: AuthenticationActionType) => {
      const userAgreements = state.get('userAgreements');
      const needsRecertification = userAgreements && checkifNeedsRecertification(userAgreements.toJS());

      return state.merge({
        hasLoggedInBefore: false,
        permissions: Immutable.fromJS(action.payload.permissions),
        timeToLogout: moment()
          .utc()
          .add(action.payload.token.expiry - 600, 's')
          .format('HH:mm'),
        userId: action.payload.user.id,
        isLoggedIn: !needsRecertification,
        fetchingAuth: !needsRecertification,
        needsRecertification: needsRecertification,
      });
    },
    SET_REDIRECT: (state: AuthenticationStoreElement, action: AuthenticationActionType) =>
      state.merge({
        redirectUrl: action.payload.redirectUrl,
      }),
    SET_REDIRECT_LOADING: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: true,
      }),
    SET_SELFIE_ERROR: (state: AuthenticationStoreElement) =>
      state.merge({
        isSelfieError: true,
      }),
    RESET_SELFIE_ERROR: (state: AuthenticationStoreElement) =>
      state.merge({
        isSelfieError: false,
      }),
    CONFIGURATION_RESPONSE: (state: AuthenticationStoreElement, action: AuthenticationActionType) =>
      state.set('configuration', Immutable.fromJS(action.payload)).set('isFetching', false),

    REQUEST_FORGOT_PASSWORD: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: true,
      }),
    RESPONSE_FORGOT_PASSWORD_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: false,
      }),
    CLEAR_FORGOT_PASSWORD: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: false,
        forgotPasswordEmailSent: false,
      }),
    RESPONSE_FORGOT_PASSWORD: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: false,
        forgotPasswordEmailSent: true,
      }),
    REQUEST_REVERIFY: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingReverify: true,
      }),
    RESPONSE_REVERIFY_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingReverify: false,
      }),
    RESPONSE_REVERIFY: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingReverify: false,
        reverifySent: true,
      }),
    CLEAR_REVERIFY: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingReverify: false,
        reverifySent: false,
      }),
    REQUEST_RESET_PASSWORD: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: true,
      }),
    RESPONSE_RESET_PASSWORD_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: false,
      }),
    RESPONSE_RESET_PASSWORD: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: false,
        isPasswordReset: true,
      }),
    AUTHENTICATION_LOGOUT_REQUEST: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: true,
        isFetchingLogout: true,
      }),
    AUTHENTICATION_LOGOUT_REQUEST_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: false,
        isFetchingLogout: false,
      }),
    AUTHENTICATION_LOGOUT: (state: AuthenticationStoreElement) =>
      state.merge({
        hasLoggedInBefore: false,
        isLoggedIn: false,
        isFetching: false,
        permissions: Immutable.List(),
        data: null,
        settings: null,
        isFetchingSettings: false,
        forgotPasswordEmailSent: false,
        isPasswordReset: false,
        timeToLogout: null,
        isFetchingLogout: false,
        needsRecertification: false,
        userId: null,
        inquiryId: null,
      }),

    SAVE_USER_DATA: (state: AuthenticationStoreElement, action: AuthenticationActionType) =>
      state.set('data', Immutable.fromJS(setUserData(action.payload.json))),
    REQUEST_USER_INFO: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: true,
      }),
    REQUEST_USER_INFO_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetching: false,
      }),
    REQUEST_USER_SETTINGS: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingSettings: true,
      }),
    REQUEST_USER_SETTINGS_SUCCESS: (state: AuthenticationStoreElement, action: AuthenticationActionType) =>
      state.merge({
        isFetchingSettings: false,
        settings: action.payload,
      }),
    REQUEST_USER_SETTINGS_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingSettings: false,
      }),
    REQUEST_SET_USER_SETTINGS: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingSettings: true,
      }),
    REQUEST_SET_USER_SETTINGS_SUCCESS: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingSettings: false,
      }),
    REQUEST_SET_USER_SETTINGS_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        isFetchingSettings: false,
      }),

    AUTHENTICATION_EXPIRE: (state: AuthenticationStoreElement) =>
      state.merge({
        hasLoggedInBefore: true,
        isFetching: false,
        permissions: Immutable.List(),
        settings: null,
        isFetchingSettings: false,
        forgotPasswordEmailSent: false,
        isPasswordReset: false,
        timeToLogout: null,
        fetchingUserAgreements: false,
        userAgreements: null,
      }),
    REQUEST_USER_AGREEMENTS: (state: AuthenticationStoreElement) =>
      state.merge({
        fetchingUserAgreements: true,
      }),
    RESPONSE_USER_AGREEMENTS_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        fetchingUserAgreements: false,
        isFetching: false,
      }),
    REQUEST_USER_CHANGE_PASSWORD: (state: AuthenticationStoreElement) =>
      state.merge({
        fetchingChangePassword: true,
      }),
    RESPONSE_USER_CHANGE_PASSWORD_FAILURE: (state: AuthenticationStoreElement) =>
      state.merge({
        fetchingChangePassword: false,
      }),
    CLEAR_CHANGE_PASSWORD_FETCH: (state: AuthenticationStoreElement) =>
      state.merge({
        fetchingChangePassword: false,
      }),
    RESPONSE_USER_CHANGE_PASSWORD_SUCCESS: (state: AuthenticationStoreElement) => state,
    RESPONSE_USER_AGREEMENTS_SUCCESS: (state: AuthenticationStoreElement, action: AuthenticationActionType) => {
      const needsRecertification = checkifNeedsRecertification(action.payload);

      const newState = state.merge({
        fetchingUserAgreements: false,
        userAgreements: Immutable.fromJS(action.payload),
        isLoggedIn: !needsRecertification,
        fetchingAuth: !needsRecertification,
        needsRecertification: needsRecertification,
        isFetching: false,
      });

      return newState;
    },
    HIDE_MESSAGE: (state: AuthenticationStoreElement) => state.set('showMessage', false),
    REQUIRE_SELFIE: (state: AuthenticationStoreElement, action: AuthenticationActionType) =>
      state.merge({
        isFetching: false,
        fetchingAuth: false,
        inquiryId: action.payload.inquiryId,
      }),
    CLEAR_SELFIE: (state: AuthenticationStoreElement) =>
      state.merge({
        inquiryId: null,
      }),
  },
  // @ts-ignore
  initialState
);

export default AuthenticationReducer;
